import ApplicationController from './application_controller';

const SELECTIZE_BIND_ATTEMPTS = 50;

/**
 * Tells the biller, before a payment form is saved, whether the chosen payment type will charge a
 * card on file through Stripe, and routes the form's submit through a confirmation modal when it
 * will. The guard sits on the form's `submit` event so every route in (the Save button, Enter in
 * a field, `requestSubmit`) is covered.
 *
 * Wrap the form and its confirmation modal in the controller element. Values:
 *   cards       - { patientPaymentMethodId: 'VISA: 4242' } for forms with a payment method select
 *   card        - the card on file for forms whose payment method is fixed
 *   modal       - CSS selector of the shared/modals/form_confirmation_modal to open before a charge
 *   stripeReady - true when a Credit card payment is routed to Stripe (the organization has a
 *                 Stripe account and the patient a Stripe customer). Credit card on a method with
 *                 no card then cannot be processed, so the form refuses to submit.
 */
export default class extends ApplicationController {
  static targets = [
    'form',
    'typeSelect',
    'methodSelect',
    'amount',
    'chargeNotice',
    'noCardNotice',
    'cardName',
    'chargeAmount',
    'submit',
  ];

  static values = { cards: Object, card: String, modal: String, stripeReady: Boolean };

  connect() {
    this.confirmed = false;
    this.selectizeHandlers = [];
    this.bindSelectize(this.typeSelectTarget);
    if (this.hasMethodSelectTarget) this.bindSelectize(this.methodSelectTarget);
    this.refresh();
  }

  disconnect() {
    this.selectizeHandlers.forEach(({ selectize, handler }) => selectize.off('change', handler));
    this.selectizeHandlers = [];
  }

  /**
   * Selectize replaces the native select and only emits jQuery events, so a Stimulus `change->`
   * action never hears a pick. Bind to the widget once it exists (it initialises on turbo:load).
   */
  bindSelectize(select, attempt = 0) {
    if (!this.element.isConnected) return;
    if (!select.selectize) {
      if (attempt < SELECTIZE_BIND_ATTEMPTS) {
        setTimeout(() => this.bindSelectize(select, attempt + 1), 100);
      }
      return;
    }
    const handler = () => this.refresh();
    select.selectize.on('change', handler);
    this.selectizeHandlers.push({ selectize: select.selectize, handler });
  }

  /**
   * Shows the charge warning when Credit card is selected and the method has a card, the
   * no-card note when Credit card is selected without one, and neither otherwise. Save is
   * disabled while the selection cannot be processed (see `blocked`).
   */
  refresh() {
    const creditCard = this.paymentType === 'credit_card';
    const { card } = this;
    if (this.hasChargeNoticeTarget) this.chargeNoticeTarget.hidden = !(creditCard && card);
    if (this.hasNoCardNoticeTarget) this.noCardNoticeTarget.hidden = !(creditCard && !card);
    if (this.hasSubmitTarget) this.submitTarget.disabled = this.blocked;
    this.cardNameTargets.forEach((element) => {
      element.textContent = card;
    });
    this.chargeAmountTargets.forEach((element) => {
      element.textContent = this.formattedAmount;
    });
  }

  /**
   * `submit` handler for the form. Lets a submission through (and locks the buttons) when no
   * card would be charged or the biller has just confirmed the charge; otherwise holds it and
   * opens the confirmation modal. A Credit card pick that cannot be processed never submits.
   */
  guard(event) {
    if (this.blocked) {
      event.preventDefault();
      return;
    }
    if (this.confirmed || !this.willChargeCard) {
      this.confirmed = false;
      this.lock();
      return;
    }
    event.preventDefault();
    this.openModal();
  }

  /**
   * Click handler for the modal's confirm button. The button is a native submit button owned by
   * the form, so its click submits the form again; this flag lets that submission through.
   */
  confirm() {
    this.confirmed = true;
  }

  /**
   * Disables every submit control for the form once a submission starts, so a second click on
   * Save or on the modal's confirm button cannot create a second payment (and a second charge).
   */
  lock() {
    this.submitControls.forEach((button) => {
      button.disabled = true;
    });
  }

  /**
   * Opens the confirmation modal. Without Bootstrap's modal plugin the form would otherwise be
   * stuck with a Save that never submits, so fall back to the browser's confirm dialog.
   */
  openModal() {
    const { card, formattedAmount: amount } = this;
    this.dispatch('confirm', { detail: { card, amount } });
    const jquery = window.jQuery || window.$;
    if (this.hasModalValue && jquery && typeof jquery.fn?.modal === 'function') {
      jquery(this.modalValue).modal('show');
      return;
    }
    // eslint-disable-next-line no-alert
    if (window.confirm(`Charge ${card} ${amount} through Stripe?`)) {
      this.confirmed = true;
      this.formTarget.requestSubmit();
    }
  }

  get willChargeCard() {
    return this.paymentType === 'credit_card' && this.card !== '';
  }

  /** Credit card on a method with no card, for a patient whose card payments go to Stripe. */
  get blocked() {
    return this.paymentType === 'credit_card' && this.card === '' && this.stripeReadyValue;
  }

  get paymentType() {
    return this.typeSelectTarget.value;
  }

  get card() {
    if (this.hasMethodSelectTarget) return this.cardsValue[this.methodSelectTarget.value] || '';
    return this.cardValue || '';
  }

  get submitControls() {
    const form = this.formTarget;
    const inside = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    const outside = form.id ? Array.from(document.querySelectorAll(`button[form="${form.id}"]`)) : [];
    return [...inside, ...outside];
  }

  get formattedAmount() {
    const raw = this.hasAmountTarget ? String(this.amountTarget.value).replace(/,/g, '') : '';
    const amount = Number.parseFloat(raw);
    if (Number.isNaN(amount)) return '';
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
}
